/**
 * The HostHandoff service is responsible for all the logic related to
 * redirecting the browser to an althernate Raven Web App domain/subdomain
 * for the case where the User signs in at the wrong domain.
 *
 * E.g. A Telus user signs in at https://telus.ravenconnected.com instead of
 * https://beta.telus.ravenconnected.com.
 *
 * It handles outgoing and incoming redirects, setting/parsing the url as
 * needed.
 **/
import CognitoUtil from 'APP_ROOT/components/aws/cognito/cognitoUtil';

const domainMapping = {
    kloud2: {
        dev: 'app.dev.klashwerks.com',
        test: 'app.test.klashwerks.com',
        qa: 'app.qa.klashwerks.com',
    },
    kloud3: {
        prod: 'app.raven.is'
    },
    telus1: {
        demo: 'demo.telus.ravenconnected.com',
        beta: 'beta.telus.ravenconnected.com',
        prod: 'telus.ravenconnected.com',
    },
}

function getMappedDomain(env, stage) {
    if (domainMapping[env] && domainMapping[env][stage]) {
        return domainMapping[env][stage];
    }
    return null;
}

function redirectOnStageHostMismatch(
    env,
    stage,
    windowLocation=window.location,
    getSessionFn=getSerializedCognitoSession,
) {
    if (process.env.REACT_APP_DISABLE_HOST_HANDOFF === 'true') return false;
    const domain = getMappedDomain(env, stage);
    if (!domain) return false;
    if (windowLocation.host === domain) return false;
    const session = getSessionFn();
    let redirectUrl = new URL(windowLocation.href);
    const [ host, port ] = domain.split(':');
    redirectUrl.host = host;
    if (port) redirectUrl.port = port;
    redirectUrl.hash = `session=${session}`;

    // console.debug('redirectUrl', redirectUrl.href);
    windowLocation.replace(redirectUrl.href);
    return true;
}

function getSerializedCognitoSession() {
    const cognitoSession = CognitoUtil.cognitoUser.getSignInUserSession();
    const sessionTokens = {
        IdToken: cognitoSession.getIdToken().getJwtToken(),
        AccessToken: cognitoSession.getAccessToken().getJwtToken(),
        RefreshToken: cognitoSession.getRefreshToken().getToken(),
    }
    // console.debug('sessionTokens', sessionTokens);
    return serializeSession(sessionTokens);
}

function serializeSession(session) {
    return encodeURIComponent(JSON.stringify(session));
}

function deserializeSession(serialized) {
    return JSON.parse(decodeURIComponent(serialized));
}

function extractSessionFromHash(hash) {
    if (!hash) return null;
    if (hash[0] === '#') hash = hash.slice(1)
    let args = hash.split("&");
    for (const arg of args) {
        const [name, value] = arg.split("=");
        if (name === "session" && value) {
            args.remove(arg);
            return {
                hash: args.join("&"),
                session: value
            }
        }
    }
    return null;
}

function signIn(tokens) {
    if (!tokens) return false;
    if (!tokens.IdToken || !tokens.AccessToken || !tokens.RefreshToken) return false;
    const user = CognitoUtil.getUser(tokens.IdToken)
    user.setSignInUserSession(user.getCognitoUserSession(tokens))
}

function trySignInWithHashSession(windowLocation=window.location) {
    const { session, hash } = extractSessionFromHash(windowLocation.hash) || {};
    if (session) {
        signIn(deserializeSession(session));
        const newUrl = new URL(windowLocation.href);
        newUrl.hash = hash;
        windowLocation.replace(newUrl.href);
        return true;
    }
    return false;
}

export default {
    redirectOnStageHostMismatch,
    trySignInWithHashSession
}
